<div
    class="vp"
    #container
    [class.vp--playing]="isPlaying"
    [class.vp--controls-visible]="controlsVisible || !isPlaying"
    (mousemove)="showControls()"
    (mouseleave)="hideControls()"
>
    <!-- ── POSTER (native video idle, or Wistia loading/error) ──────────── -->
    <div *ngIf="(isIdle && !isWistia) || (isWistia && !wistiaScriptReady)" class="vp__poster" (click)="onPosterClick()">
        <div class="vp__poster-header">
            <div class="vp__favicon">
                <img [src]="faviconUrl" alt="FinanceOS" width="24" height="24" />
            </div>
            <span class="vp__poster-title">{{ title }}</span>
        </div>

        <div class="vp__poster-center">
            <img class="vp__poster-logo" [src]="posterLogoUrl" alt="FinanceOS" />

            <div *ngIf="videoUrl" class="vp__play-badge" aria-label="Play">
                <i class="dr-icon-play"></i>
            </div>
        </div>
    </div>

    <!-- ── WISTIA PLAYER ────────────────────────────────────────────────── -->
    <div
        *ngIf="isWistia && wistiaVideoId && wistiaScriptReady"
        class="vp__wistia wistia_embed"
        [ngClass]="'wistia_async_' + wistiaVideoId"
    ></div>

    <!-- ── VIDEO ──────────────────────────────────────────────────────────── -->
    <video
        *ngIf="!isIdle && !isWistia"
        #videoEl
        class="vp__video"
        [src]="videoUrl"
        (loadedmetadata)="onLoadedMetadata($event)"
        (timeupdate)="onTimeUpdate($event)"
        (ended)="onEnded()"
        (pause)="onVideoPause()"
        (play)="onVideoPlay()"
    ></video>

    <!-- ── CONTROLS OVERLAY ───────────────────────────────────────────────── -->
    <div *ngIf="!isIdle && !isWistia" class="vp__overlay">
        <button class="vp__center-btn" (click)="togglePlay()" [attr.aria-label]="isPlaying ? 'Pause' : 'Play'">
            <svg *ngIf="state === 'ended'" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path
                    d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"
                />
            </svg>
            <svg *ngIf="isPlaying" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
            </svg>
            <svg *ngIf="state === 'paused'" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
            </svg>
        </button>

        <div class="vp__controls">
            <div class="vp__progress" (click)="seekTo($event)">
                <div class="vp__progress-buffered" [style.width.%]="bufferedPercent"></div>
                <div class="vp__progress-played" [style.width.%]="progressPercent">
                    <div class="vp__progress-thumb"></div>
                </div>
            </div>

            <div class="vp__controls-row">
                <button class="vp__ctrl-btn" (click)="togglePlay()" [attr.aria-label]="isPlaying ? 'Pause' : 'Play'">
                    <svg *ngIf="isPlaying" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </svg>
                    <svg *ngIf="!isPlaying" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                </button>

                <span class="vp__time">{{ formattedCurrent }} / {{ formattedDuration }}</span>
                <div class="vp__spacer"></div>

                <button class="vp__ctrl-btn" (click)="toggleMute()" aria-label="Toggle mute">
                    <svg *ngIf="!muted && volume > 0" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"
                        />
                    </svg>
                    <svg *ngIf="muted || volume === 0" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path
                            d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"
                        />
                    </svg>
                </button>
                <input
                    class="vp__volume-slider"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    [value]="muted ? 0 : volume"
                    (input)="setVolume($event)"
                    aria-label="Volume"
                />

                <button class="vp__ctrl-btn" (click)="toggleFullscreen(container)" aria-label="Fullscreen">
                    <svg *ngIf="!isFullscreen" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                    </svg>
                    <svg *ngIf="isFullscreen" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</div>
